<template>
    <div class="testdepo-setup">
        <h3>Настройка интеграции</h3>

        <div class="setup-form">
            <div class="form-row">
                <label for="testId">ID тестирования:</label>
                <InputText
                    id="testId"
                    v-model="testId"
                    type="number"
                    placeholder="Введите ID тестирования"
                    class="p-inputtext-sm"
                />
            </div>

            <PrimeButton
                label="Загрузить поля"
                icon="pi pi-download"
                class="p-button-sm"
                :loading="loading"
                @click="loadFields"
            />
        </div>

        <div v-if="error" class="error-message">
            <i class="pi pi-exclamation-circle"></i>
            {{ error }}
        </div>

        <div v-if="fields.length > 0" class="fields-preview">
            <div class="fields-header">
                <h4>Поля TestDepo</h4>
                <span class="selected-count">Выбрано {{ selectedCount }} из {{ fields.length }}</span>
            </div>

            <DataTable :value="fields" responsiveLayout="scroll" class="p-datatable-sm">
                <TableColumn headerStyle="width: 52px" bodyStyle="text-align: center">
                    <template #header>
                        <input
                            type="checkbox"
                            :checked="allSelected"
                            :aria-label="allSelected ? 'Снять выбор со всех полей' : 'Выбрать все поля'"
                            @change="toggleAll"
                        />
                    </template>
                    <template #body="{ index }">
                        <input
                            type="checkbox"
                            :checked="isSelected(index)"
                            :aria-label="'Выбрать поле ' + fields[index].key"
                            @change="toggleField(index)"
                        />
                    </template>
                </TableColumn>
                <TableColumn field="key" header="Имя поля"></TableColumn>
                <TableColumn field="source" header="Источник">
                    <template #body="{ data }">
                        {{ data.source === 'user_form' ? 'Анкета пользователя' : 'Система' }}
                    </template>
                </TableColumn>
                <TableColumn field="label" header="Описание"></TableColumn>
            </DataTable>

            <div v-if="validationError" class="validation-error">
                {{ validationError }}
            </div>

            <div class="action-buttons">
                <PrimeButton
                    label="Применить выбранные поля"
                    icon="pi pi-check"
                    class="p-button-primary"
                    :disabled="selectedCount === 0 || applying"
                    :loading="applying"
                    @click="applySelectedFields"
                />
            </div>
        </div>
    </div>
</template>

<script>
import api from "@/libs/api";

export default {
    name: "TestDepoSetup",
    props: {
        projectId: {
            type: Number,
            required: true
        }
    },
    emits: ['fields-loaded', 'needs-confirmation'],
    data() {
        return {
            testId: null,
            loading: false,
            applying: false,
            error: null,
            validationError: null,
            fields: [],
            selectedIndexes: []
        };
    },
    computed: {
        selectedCount() {
            return this.selectedIndexes.length;
        },
        allSelected() {
            return this.fields.length > 0 && this.selectedIndexes.length === this.fields.length;
        },
        selectedFieldNames() {
            return this.selectedIndexes
                .map(index => this.fields[index])
                .filter(field => field && field.key)
                .map(field => field.key);
        }
    },
    methods: {
        async loadFields() {
            if (!this.testId) {
                this.error = 'Введите ID тестирования';
                return;
            }

            this.loading = true;
            this.error = null;
            this.validationError = null;

            try {
                const result = await api.fetchTestDepoFields(this.projectId, this.testId);
                this.fields = result.fields || [];
                this.selectedIndexes = [];
                if (this.fields.length === 0) {
                    this.error = 'TestDepo не вернул поля для этого тестирования.';
                }
            } catch (err) {
                if (err.response && err.response.data.message) {
                    this.error = err.response.data.message;
                } else {
                    this.error = 'Не удалось загрузить поля. Проверьте ID тестирования и попробуйте снова.';
                }
            } finally {
                this.loading = false;
            }
        },
        isSelected(index) {
            return this.selectedIndexes.includes(index);
        },
        toggleField(index) {
            if (this.isSelected(index)) {
                this.selectedIndexes = this.selectedIndexes.filter(item => item !== index);
            } else {
                this.selectedIndexes = [...this.selectedIndexes, index].sort((a, b) => a - b);
            }
            this.validationError = null;
        },
        toggleAll() {
            if (this.allSelected) {
                this.selectedIndexes = [];
            } else {
                this.selectedIndexes = Array.from(Array(this.fields.length).keys());
            }
            this.validationError = null;
        },
        validateSelection() {
            this.validationError = null;

            if (this.selectedFieldNames.length === 0) {
                this.validationError = 'Выберите хотя бы одно поле';
                return false;
            }

            const duplicates = this.selectedFieldNames.filter((name, index, names) => names.indexOf(name) !== index);
            if (duplicates.length > 0) {
                this.validationError = 'Среди выбранных полей есть повторяющиеся имена';
                return false;
            }

            return true;
        },
        async applySelectedFields() {
            if (!this.validateSelection()) return;

            this.applying = true;
            this.error = null;

            try {
                const result = await api.createDataKeys(this.projectId, this.selectedFieldNames);
                this.$emit('fields-loaded', {
                    keys: result.keys || [],
                    test_title: null
                });
            } catch (err) {
                if (err.response && err.response.data.error === 'data_replacement_requires_confirmation') {
                    this.$emit('needs-confirmation', {
                        currentRowCount: err.response.data.current_row_count,
                        action: 'change_keys',
                        fields: this.selectedFieldNames
                    });
                } else if (err.response && err.response.data.message) {
                    this.error = err.response.data.message;
                } else {
                    this.error = 'Не удалось применить выбранные поля.';
                }
            } finally {
                this.applying = false;
            }
        }
    }
}
</script>

<style scoped>
.testdepo-setup {
    max-width: 600px;
    margin: 0 auto;
    padding: 24px;
    background: #f8f9fa;
    border-radius: 8px;
}

h3 {
    text-align: center;
    margin-bottom: 24px;
}

.setup-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
}

.form-row {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
}

.form-row label {
    min-width: 140px;
    font-weight: 500;
}

.form-row input {
    flex: 1;
}

.error-message {
    margin-top: 16px;
    padding: 12px;
    background: #f8d7da;
    border-radius: 4px;
    color: #721c24;
    display: flex;
    align-items: center;
    gap: 8px;
}

.fields-preview {
    margin-top: 24px;
}

.fields-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
}

.fields-header h4 {
    margin: 0;
}

.selected-count {
    color: #6c757d;
    font-size: 0.9rem;
}

.validation-error {
    color: #dc3545;
    font-size: 0.875rem;
    margin: 12px 0 0;
    padding: 8px;
    background: #f8d7da;
    border-radius: 4px;
}

.action-buttons {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
}
</style>
